import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { inputStyle, bigBtn, smallBtn, formOverlay, formContainer, sectionLabel, tableStyle, thStyle, trStyle } from '../lib/styles';

const BOARDS = [
  { name:'DAT',           url:'https://one.dat.com' },
  { name:'TRUCKSTOP',     url:'https://app.truckstop.com' },
  { name:'COYOTE',        url:'https://logistics.coyote.com' },
  { name:'SYLECTUS',      url:'https://www.sylectus.com' },
  { name:'AMAZON_RELAY',  url:'https://relay.amazon.com' },
  { name:'UBER_FREIGHT',  url:'https://www.uberfreight.com' },
];

const FUEL_CARD_PROVIDERS = [
  { value:'COMDATA',  label:'Comdata',       apiKeyLabel:'API Key + Account ID', helpUrl:'https://developer.comdata.com' },
  { value:'WEX',      label:'WEX (EFS)',      apiKeyLabel:'API Key',             helpUrl:'https://www.wexinc.com/solutions/fleet-cards/wex-efs' },
  { value:'EFS',      label:'EFS (FleetOne)', apiKeyLabel:'API Key',             helpUrl:'https://www.efs.io' },
  { value:'PILOT',    label:'Pilot / Flying J', apiKeyLabel:'Account Number',   helpUrl:'https://www.pilotflyingj.com/mypilotnow' },
  { value:'LOVES',    label:"Love's",         apiKeyLabel:'Account Number',      helpUrl:'https://www.loves.com/en/fuel-cards' },
  { value:'MANUAL',   label:'Manual Entry',   apiKeyLabel:'N/A',                 helpUrl:'' },
];

const FACTORING_PROVIDERS = [
  { value:'OTR',     label:'OTR Capital',          apiKeyLabel:'API Key',            helpUrl:'https://www.otrcapital.com/carriers' },
  { value:'RTS',     label:'RTS Financial',         apiKeyLabel:'API Key',            helpUrl:'https://www.rtsfinancial.com/carriers' },
  { value:'TRIUMPH', label:'Triumph Business Capital', apiKeyLabel:'API Key',        helpUrl:'https://www.triumphbusiness.com' },
  { value:'TAB',     label:'TAB Bank',              apiKeyLabel:'API Key',            helpUrl:'https://www.tabbank.com/factoring' },
  { value:'EMAIL',   label:'Other (Submit by Email)', apiKeyLabel:'Factor Email Address', helpUrl:'' },
];

const ELD_PROVIDERS = [
  { value:'MOTIVE',          label:'Motive (KeepTruckin)',    apiKeyLabel:'API Key',                    helpUrl:'https://developer.gomotive.com/docs/api-key' },
  { value:'SAMSARA',         label:'Samsara',                  apiKeyLabel:'API Token',                  helpUrl:'https://developers.samsara.com/docs/authentication' },
  { value:'GEOTAB',          label:'Geotab / MyGeotab',        apiKeyLabel:'database|username|password', helpUrl:'https://developers.geotab.com/myGeotab/apiReference' },
  { value:'VERIZON_CONNECT', label:'Verizon Connect',          apiKeyLabel:'account|username|password',  helpUrl:'https://dev.fleetmatics.com' },
  { value:'MANUAL',          label:'Manual / No ELD',          apiKeyLabel:'N/A',                        helpUrl:'' },
];

const emptyCompany = { name:'', ops_email:'', sales_team_email:'' };

interface SettingsProps {
  companyId: string | null;
}

export default function Settings({ companyId }: SettingsProps) {
  const [creds, setCreds] = useState<any[]>([]);
  const [company, setCompany] = useState(emptyCompany);
  const [showAddCred, setShowAddCred] = useState(false);
  const [newBoard, setNewBoard] = useState('DAT');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [savingCred, setSavingCred] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [companySaved, setCompanySaved] = useState(false);

  // ELD state
  // Fuel card state
  const [fuelCardProvider, setFuelCardProvider] = useState('MANUAL');
  const [fuelCardApiKey, setFuelCardApiKey] = useState('');
  const [fuelCardSaving, setFuelCardSaving] = useState(false);
  const [fuelCardSaved, setFuelCardSaved] = useState(false);

  // Factoring state
  const [factoringProvider, setFactoringProvider] = useState('OTR');
  const [factoringApiKey, setFactoringApiKey] = useState('');
  const [factoringSaving, setFactoringSaving] = useState(false);
  const [factoringSaved, setFactoringSaved] = useState(false);

  // EDI state
  const [ediSenderId, setEdiSenderId] = useState('');
  const [ediEnabled, setEdiEnabled] = useState(false);
  const [ediSaving, setEdiSaving] = useState(false);
  const [ediSaved, setEdiSaved] = useState(false);

  const [eldProvider, setEldProvider] = useState('MOTIVE');
  const [eldApiKey, setEldApiKey] = useState('');
  const [eldSaving, setEldSaving] = useState(false);
  const [eldSaved, setEldSaved] = useState(false);
  const [eldTesting, setEldTesting] = useState(false);
  const [eldTestResult, setEldTestResult] = useState<{ok:boolean;msg:string}|null>(null);

  useEffect(() => {
    if (!companyId) return;
    fetchCreds();
    fetchCompany();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchCreds = async () => {
    const { data } = await supabase.from('load_board_credentials').select('id,board_name,username,is_active,last_used,created_at').eq('company_id', companyId).order('board_name');
    setCreds(data || []);
  };

  const fetchCompany = async () => {
    const { data } = await supabase.from('companies').select('name,ops_email,sales_team_email,eld_provider,eld_api_key,edi_sender_id,edi_enabled,factoring_provider,factoring_api_key,fuel_card_provider,fuel_card_api_key').eq('id', companyId).maybeSingle();
    if (data) {
      setCompany({ name:data.name||'', ops_email:data.ops_email||'', sales_team_email:data.sales_team_email||'' });
      setEldProvider(data.eld_provider || 'MOTIVE');
      setEldApiKey(data.eld_api_key ? '••••••••••••••••' : '');
      setEdiSenderId(data.edi_sender_id || '');
      setEdiEnabled(data.edi_enabled || false);
      setFuelCardProvider(data.fuel_card_provider || 'MANUAL');
      setFuelCardApiKey(data.fuel_card_api_key ? '••••••••••••••••' : '');
      setFactoringProvider(data.factoring_provider || 'OTR');
      setFactoringApiKey(data.factoring_api_key ? '••••••••••••••••' : '');
    }
  };

  const handleSaveFuelCard = async () => {
    if (!companyId) return;
    setFuelCardSaving(true);
    const updates: any = { fuel_card_provider: fuelCardProvider };
    if (fuelCardApiKey && !fuelCardApiKey.startsWith('••')) updates.fuel_card_api_key = fuelCardApiKey;
    await supabase.from('companies').update(updates).eq('id', companyId);
    setFuelCardSaving(false); setFuelCardSaved(true);
    setTimeout(() => setFuelCardSaved(false), 2500);
  };

  const handleSaveFactoring = async () => {
    if (!companyId) return;
    setFactoringSaving(true);
    const updates: any = { factoring_provider: factoringProvider };
    if (factoringApiKey && !factoringApiKey.startsWith('••')) updates.factoring_api_key = factoringApiKey;
    await supabase.from('companies').update(updates).eq('id', companyId);
    setFactoringSaving(false); setFactoringSaved(true);
    setTimeout(() => setFactoringSaved(false), 2500);
  };

  const handleSaveEDI = async () => {
    if (!companyId) return;
    setEdiSaving(true);
    await supabase.from('companies').update({ edi_sender_id: ediSenderId.trim().toUpperCase() || null, edi_enabled: ediEnabled }).eq('id', companyId);
    setEdiSaving(false); setEdiSaved(true);
    setTimeout(() => setEdiSaved(false), 2500);
  };

  const handleSaveELD = async () => {
    if (!companyId) return;
    setEldSaving(true);
    const updates: any = { eld_provider: eldProvider };
    if (eldApiKey && !eldApiKey.startsWith('••')) updates.eld_api_key = eldApiKey;
    await supabase.from('companies').update(updates).eq('id', companyId);
    setEldSaving(false); setEldSaved(true);
    setTimeout(() => setEldSaved(false), 2500);
  };

  const handleTestELD = async () => {
    if (!companyId) return;
    setEldTesting(true); setEldTestResult(null);
    const { error } = await supabase.functions.invoke('eld-sync', { body: { companyId } });
    if (error) setEldTestResult({ ok:false, msg: error.message });
    else setEldTestResult({ ok:true, msg:'Connection successful. Driver GPS and HOS updated.' });
    setEldTesting(false);
  };

  const handleSaveCred = async () => {
    if (!companyId || !newUsername.trim() || !newPassword.trim()) { alert("Username and password required."); return; }
    setSavingCred(true);
    const board = BOARDS.find(b => b.name === newBoard)!;
    // Encrypt password via edge function so the key never leaves server
    const { data: encrypted, error: encErr } = await supabase.functions.invoke('encrypt-credential', { body: { value: newPassword } });
    const passwordEnc = encrypted?.encrypted || btoa(newPassword); // fallback: base64 (not secure — set CREDENTIAL_ENCRYPTION_KEY)
    if (encErr) console.warn('Encryption edge function not deployed — using base64 fallback. Set CREDENTIAL_ENCRYPTION_KEY in Supabase secrets.');
    const { error } = await supabase.from('load_board_credentials').insert({ company_id:companyId, board_name:newBoard, board_url:board.url, username:newUsername.trim(), password_enc:passwordEnc, is_active:true });
    if (error) { alert(`Save failed: ${error.message}`); setSavingCred(false); return; }
    setShowAddCred(false); setNewUsername(''); setNewPassword(''); setNewBoard('DAT');
    fetchCreds();
    setSavingCred(false);
  };

  const handleDeleteCred = async (id: string) => {
    if (!window.confirm("Remove this credential?")) return;
    await supabase.from('load_board_credentials').delete().eq('id', id);
    fetchCreds();
  };

  const handleToggleCred = async (id: string, current: boolean) => {
    await supabase.from('load_board_credentials').update({ is_active: !current }).eq('id', id);
    fetchCreds();
  };

  const handleSaveCompany = async () => {
    if (!companyId) return;
    setSavingCompany(true);
    await supabase.from('companies').update({ ops_email: company.ops_email||null, sales_team_email: company.sales_team_email||null }).eq('id', companyId);
    setSavingCompany(false);
    setCompanySaved(true);
    setTimeout(() => setCompanySaved(false), 2500);
  };


  return (
    <>
      {showAddCred && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"420px" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>ADD LOAD BOARD CREDENTIALS</h3>
            <div style={sectionLabel}>LOAD BOARD</div>
            <select style={inputStyle as any} value={newBoard} onChange={e=>setNewBoard(e.target.value)}>
              {BOARDS.map(b=><option key={b.name} value={b.name}>{b.name.replace(/_/g,' ')}</option>)}
            </select>
            <div style={{ color:"#555", fontSize:"11px", marginBottom:"8px" }}>Login URL: {BOARDS.find(b=>b.name===newBoard)?.url}</div>
            <div style={sectionLabel}>CREDENTIALS</div>
            <input type="text" placeholder="Username / Email" style={inputStyle} value={newUsername} onChange={e=>setNewUsername(e.target.value)} />
            <input type="password" placeholder="Password" style={inputStyle} value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
            <div style={{ color:"#555", fontSize:"11px", marginTop:"4px", marginBottom:"8px" }}>Password is encrypted at rest using AES-256. It is never shown again after saving.</div>
            <div style={{ display:"flex", gap:"10px", marginTop:"12px" }}>
              <button onClick={handleSaveCred} disabled={savingCred} style={bigBtn}>{savingCred?"SAVING...":"SAVE CREDENTIALS"}</button>
              <button onClick={()=>setShowAddCred(false)} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* LOAD BOARD CREDENTIALS */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505", marginBottom:"24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700 }}>LOAD BOARD CREDENTIALS</div>
          <button onClick={()=>setShowAddCred(true)} style={smallBtn}>+ ADD CREDENTIALS</button>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>BOARD</th>
              <th style={thStyle}>USERNAME</th>
              <th style={thStyle}>STATUS</th>
              <th style={thStyle}>LAST USED</th>
              <th style={thStyle}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {BOARDS.map(board => {
              const cred = creds.find(c => c.board_name === board.name);
              return (
                <tr key={board.name} style={trStyle}>
                  <td style={{ padding:"10px 12px", fontWeight:700 }}>{board.name.replace(/_/g,' ')}</td>
                  <td style={{ padding:"10px 12px", color:"#aaa" }}>{cred ? cred.username : <span style={{color:"#333"}}>—</span>}</td>
                  <td style={{ padding:"10px 12px" }}>
                    {cred
                      ? <span style={{ color:cred.is_active?'#32CD32':'#555', fontWeight:700, fontSize:"10px" }}>{cred.is_active?'✓ ACTIVE':'PAUSED'}</span>
                      : <span style={{ color:"#333", fontSize:"10px" }}>NOT CONFIGURED</span>
                    }
                  </td>
                  <td style={{ padding:"10px 12px", color:"#555", fontSize:"11px" }}>{cred?.last_used ? new Date(cred.last_used).toLocaleDateString() : '—'}</td>
                  <td style={{ padding:"10px 12px", display:"flex", gap:"6px" }}>
                    {cred
                      ? <>
                          <button onClick={()=>handleToggleCred(cred.id, cred.is_active)} style={{ ...smallBtn, background:"transparent", border:"1px solid #555", color:"#aaa" }}>{cred.is_active?'PAUSE':'ACTIVATE'}</button>
                          <button onClick={()=>handleDeleteCred(cred.id)} style={{ ...smallBtn, background:"transparent", border:"1px solid #ff4444", color:"#ff4444" }}>REMOVE</button>
                        </>
                      : <button onClick={()=>{setNewBoard(board.name);setShowAddCred(true);}} style={smallBtn}>+ ADD</button>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ color:"#555", fontSize:"11px", marginTop:"12px" }}>
          Passwords are AES-256 encrypted at rest. Jake uses these credentials to hunt loads when you ask or when a driver delivers with no next load. Set <code style={{color:"#32CD32"}}>CREDENTIAL_ENCRYPTION_KEY</code> in Supabase secrets to enable full encryption.
        </div>
      </div>

      {/* COMPANY INFO */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505", marginBottom:"24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700 }}>COMPANY INFO & NOTIFICATIONS</div>
          <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
            {companySaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
            <button onClick={handleSaveCompany} disabled={savingCompany} style={smallBtn}>{savingCompany?"SAVING...":"SAVE"}</button>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px" }}>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>OPS EMAIL</div>
            <div style={{ color:"#555", fontSize:"11px", marginBottom:"4px" }}>Used by Jake for delay notifications and alerts</div>
            <input type="email" placeholder="ops@yourcompany.com" style={inputStyle} value={company.ops_email} onChange={e=>setCompany({...company,ops_email:e.target.value})} />
          </div>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>SALES TEAM EMAIL</div>
            <div style={{ color:"#555", fontSize:"11px", marginBottom:"4px" }}>Receives Jake's daily CRM digest at 6 PM</div>
            <input type="email" placeholder="sales@yourcompany.com" style={inputStyle} value={company.sales_team_email} onChange={e=>setCompany({...company,sales_team_email:e.target.value})} />
          </div>
        </div>
      </div>

      {/* EMAIL → LOAD AUTOMATION */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505", marginBottom:"24px" }}>
        <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700, marginBottom:"4px" }}>EMAIL → LOAD AUTOMATION</div>
        <div style={{ color:"#555", fontSize:"11px", marginBottom:"16px" }}>Brokers email rate confirmations to your inbound address. Claude reads the email or PDF attachment and creates the load in Control Tower automatically.</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"16px" }}>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>YOUR INBOUND EMAIL ADDRESS</div>
            <div style={{ color:"#555", fontSize:"10px", marginBottom:"4px" }}>Give this to every broker you work with</div>
            <div style={{ background:"#0a0a0a", border:"1px solid #32CD32", padding:"12px", fontFamily:"monospace", fontSize:"13px", color:"#32CD32", letterSpacing:"1px" }}>
              {company.name ? `${company.name.toLowerCase().replace(/[^a-z0-9]/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'')}@loads.blacktopsystem.com` : 'loads@blacktopsystem.com'}
            </div>
            <div style={{ color:"#555", fontSize:"10px", marginTop:"4px" }}>Your personalized inbound email. Generic: loads@blacktopsystem.com also works.</div>
          </div>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>WHAT IT HANDLES</div>
            <div style={{ color:"#555", fontSize:"11px", lineHeight:"1.8" }}>
              ✓ Plain text rate confirmations<br/>
              ✓ HTML emails from broker TMS (Echo, Coyote, TQL)<br/>
              ✓ PDF rate confirmation attachments<br/>
              ✓ Forwarded emails from your team
            </div>
          </div>
        </div>
        <div style={{ background:"#0a0a0a", border:"1px solid #1a1a1a", padding:"14px", marginBottom:"12px" }}>
          <div style={{ color:"#32CD32", fontSize:"10px", fontWeight:700, marginBottom:"10px" }}>ONE-TIME SETUP (DNS + SendGrid)</div>
          <div style={{ color:"#555", fontSize:"11px", lineHeight:"1.8" }}>
            1. Add MX record: <span style={{color:"#aaa"}}>loads.blacktopsystem.com → mx.sendgrid.net (priority 10)</span><br/>
            2. In SendGrid: Settings → Inbound Parse → Add Host & URL<br/>
            &nbsp;&nbsp;&nbsp;Hostname: <span style={{color:"#aaa"}}>loads.blacktopsystem.com</span><br/>
            &nbsp;&nbsp;&nbsp;URL: <span style={{color:"#aaa", wordBreak:"break-all" as any}}>https://lclkrlspnmhdzotzvipb.supabase.co/functions/v1/email-inbound</span><br/>
            3. Check "Post the raw, full MIME message"<br/>
            4. That's it — every email to loads@blacktopsystem.com becomes a load
          </div>
        </div>
        <div style={{ color:"#555", fontSize:"11px" }}>
          Jake fires a GREEN alert for every new load created by email. Loads appear in Control Tower with source "EMAIL" so you know they came in automatically.
        </div>
      </div>

      {/* FUEL CARD */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505", marginBottom:"24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <div>
            <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700 }}>FUEL CARD</div>
            <div style={{ color:"#555", fontSize:"11px", marginTop:"4px" }}>Auto-import fuel transactions. Eliminates manual fuel log entry.</div>
          </div>
          <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
            {fuelCardSaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
            <button onClick={handleSaveFuelCard} disabled={fuelCardSaving} style={smallBtn}>{fuelCardSaving?'SAVING...':'SAVE'}</button>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px" }}>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>PROVIDER</div>
            <select style={inputStyle as any} value={fuelCardProvider} onChange={e=>{ setFuelCardProvider(e.target.value); setFuelCardApiKey(''); }}>
              {FUEL_CARD_PROVIDERS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>
              {FUEL_CARD_PROVIDERS.find(p=>p.value===fuelCardProvider)?.apiKeyLabel || 'API KEY'}
              {FUEL_CARD_PROVIDERS.find(p=>p.value===fuelCardProvider)?.helpUrl &&
                <a href={FUEL_CARD_PROVIDERS.find(p=>p.value===fuelCardProvider)!.helpUrl} target="_blank" rel="noreferrer" style={{ color:"#555", marginLeft:"8px", fontSize:"10px" }}>↗ Get key</a>}
            </div>
            {fuelCardProvider === 'MANUAL'
              ? <div style={{ color:"#555", fontSize:"11px", padding:"10px 0" }}>Fuel logs entered manually in the Fuel room.</div>
              : <input type="password" placeholder="Paste key here" style={inputStyle} value={fuelCardApiKey.startsWith('••')?'':fuelCardApiKey} onChange={e=>setFuelCardApiKey(e.target.value)} />
            }
          </div>
        </div>
      </div>

      {/* FACTORING */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505", marginBottom:"24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <div>
            <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700 }}>FACTORING COMPANY</div>
            <div style={{ color:"#555", fontSize:"11px", marginTop:"4px" }}>One-click invoice submission on delivered loads. Plug in any factor.</div>
          </div>
          <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
            {factoringSaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
            <button onClick={handleSaveFactoring} disabled={factoringSaving} style={smallBtn}>{factoringSaving?'SAVING...':'SAVE'}</button>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"12px" }}>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>PROVIDER</div>
            <select style={inputStyle as any} value={factoringProvider} onChange={e=>{ setFactoringProvider(e.target.value); setFactoringApiKey(''); }}>
              {FACTORING_PROVIDERS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>
              {FACTORING_PROVIDERS.find(p=>p.value===factoringProvider)?.apiKeyLabel || 'API KEY / EMAIL'}
              {FACTORING_PROVIDERS.find(p=>p.value===factoringProvider)?.helpUrl &&
                <a href={FACTORING_PROVIDERS.find(p=>p.value===factoringProvider)!.helpUrl} target="_blank" rel="noreferrer" style={{ color:"#555", marginLeft:"8px", fontSize:"10px" }}>↗ Get key</a>}
            </div>
            <input
              type={factoringProvider === 'EMAIL' ? 'email' : 'password'}
              placeholder={factoringProvider === 'EMAIL' ? 'factor@company.com' : 'Paste API key'}
              style={inputStyle}
              value={factoringApiKey.startsWith('••') ? '' : factoringApiKey}
              onChange={e=>setFactoringApiKey(e.target.value)}
            />
          </div>
        </div>
        <div style={{ color:"#555", fontSize:"11px" }}>For factors without an API, choose "Other" and enter their email — Jake will submit invoices by email with full load details.</div>
      </div>

      {/* EDI */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505", marginBottom:"24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <div>
            <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700 }}>EDI — ELECTRONIC DATA INTERCHANGE</div>
            <div style={{ color:"#555", fontSize:"11px", marginTop:"4px" }}>Inbound 204 load tenders auto-create loads. Outbound 214 status updates sent on every workflow change.</div>
          </div>
          <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
            {ediSaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
            <button onClick={handleSaveEDI} disabled={ediSaving} style={smallBtn}>{ediSaving?'SAVING...':'SAVE'}</button>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"16px" }}>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>YOUR ISA SENDER ID</div>
            <div style={{ color:"#555", fontSize:"10px", marginBottom:"4px" }}>Up to 15 characters. Give this to brokers so they can send you tenders.</div>
            <input type="text" placeholder="e.g. BLACKTOP01" maxLength={15} style={inputStyle} value={ediSenderId} onChange={e=>setEdiSenderId(e.target.value.toUpperCase())} />
          </div>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>INBOUND WEBHOOK URL</div>
            <div style={{ color:"#555", fontSize:"10px", marginBottom:"4px" }}>Give this URL to Stedi or your broker's EDI team</div>
            <div style={{ background:"#0a0a0a", border:"1px solid #1a1a1a", padding:"10px", fontFamily:"monospace", fontSize:"10px", color:"#32CD32", wordBreak:"break-all" as any }}>
              https://lclkrlspnmhdzotzvipb.supabase.co/functions/v1/edi-receiver
            </div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"16px" }}>
          <button onClick={()=>setEdiEnabled(!ediEnabled)} style={{ background:"transparent", border:`1px solid ${ediEnabled?'#32CD32':'#333'}`, color:ediEnabled?'#32CD32':'#555', padding:"8px 16px", fontFamily:"monospace", fontSize:"11px", cursor:"pointer" }}>
            {ediEnabled ? '✓ EDI ENABLED' : 'EDI DISABLED'}
          </button>
          <span style={{ color:"#555", fontSize:"11px" }}>When enabled, inbound 204s auto-create loads in Control Tower</span>
        </div>
        <div style={{ background:"#0a0a0a", border:"1px solid #1a1a1a", padding:"12px" }}>
          <div style={{ color:"#32CD32", fontSize:"10px", fontWeight:700, marginBottom:"8px" }}>SETUP GUIDE</div>
          <div style={{ color:"#555", fontSize:"11px", lineHeight:"1.6" }}>
            1. Create a free account at <span style={{color:"#aaa"}}>stedi.com</span><br/>
            2. Add a trading partner for each broker (DAT, Coyote, Echo, etc.)<br/>
            3. Point their webhook to the URL above<br/>
            4. Set <span style={{color:"#32CD32"}}>STEDI_API_KEY</span> in Supabase secrets for outbound 214 responses<br/>
            5. Give brokers your ISA Sender ID above
          </div>
        </div>
      </div>

      {/* ELD / TELEMATICS */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505", marginBottom:"24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <div>
            <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700 }}>ELD / TELEMATICS PROVIDER</div>
            <div style={{ color:"#555", fontSize:"11px", marginTop:"4px" }}>Plug in any ELD — GPS and HOS sync automatically every 15 min</div>
          </div>
          <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
            {eldSaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
            {eldTestResult && <span style={{ color:eldTestResult.ok?'#32CD32':'#ff4444', fontSize:"11px" }}>{eldTestResult.ok?'✓':'✗'} {eldTestResult.msg}</span>}
            <button onClick={handleTestELD} disabled={eldTesting} style={{ ...smallBtn, background:"transparent", border:"1px solid #32CD32", color:"#32CD32" }}>{eldTesting?'TESTING...':'TEST CONNECTION'}</button>
            <button onClick={handleSaveELD} disabled={eldSaving} style={smallBtn}>{eldSaving?'SAVING...':'SAVE'}</button>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px" }}>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>PROVIDER</div>
            <select style={inputStyle as any} value={eldProvider} onChange={e=>{ setEldProvider(e.target.value); setEldApiKey(''); setEldTestResult(null); }}>
              {ELD_PROVIDERS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ color:"#777", fontSize:"11px", marginBottom:"6px" }}>
              {ELD_PROVIDERS.find(p=>p.value===eldProvider)?.apiKeyLabel || 'API KEY'}
              {ELD_PROVIDERS.find(p=>p.value===eldProvider)?.helpUrl &&
                <a href={ELD_PROVIDERS.find(p=>p.value===eldProvider)!.helpUrl} target="_blank" rel="noreferrer" style={{ color:"#555", marginLeft:"8px", fontSize:"10px" }}>↗ How to find</a>}
            </div>
            {eldProvider === 'MANUAL'
              ? <div style={{ color:"#555", fontSize:"11px", padding:"10px 0" }}>No API key needed. GPS and HOS entered manually.</div>
              : <input type="password" placeholder="Paste key here (existing key is hidden)" style={inputStyle} value={eldApiKey.startsWith('••')?'':eldApiKey} onChange={e=>setEldApiKey(e.target.value)} />
            }
          </div>
        </div>
        <div style={{ marginTop:"16px", display:"flex", gap:"12px", flexWrap:"wrap" as any }}>
          {ELD_PROVIDERS.filter(p=>p.value!=='MANUAL').map(p=>(
            <div key={p.value} style={{ background:"#0a0a0a", border:`1px solid ${eldProvider===p.value?'#32CD32':'#1a1a1a'}`, padding:"8px 14px", fontSize:"11px", color:eldProvider===p.value?'#32CD32':'#555', cursor:"pointer" }} onClick={()=>{ setEldProvider(p.value); setEldApiKey(''); setEldTestResult(null); }}>
              {p.label}
            </div>
          ))}
        </div>
      </div>

      {/* JAKE CONFIG */}
      <div style={{ border:"1px solid #1e1e1e", padding:"20px", background:"#050505" }}>
        <div style={{ color:"#32CD32", fontSize:"11px", letterSpacing:"2px", fontWeight:700, marginBottom:"16px" }}>JAKE CONFIGURATION</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"16px" }}>
          {[
            { label:"Delay Alert Threshold", val:"2 hours overdue", note:"Time after delivery date before Jake alerts" },
            { label:"Mileage Alert Threshold", val:"< 500 miles remaining", note:"Miles before service due to alert" },
            { label:"Compliance Alert Window", val:"30 days", note:"Days before expiry to start alerting" },
          ].map((k,i)=>(
            <div key={i} style={{ background:"#0a0a0a", border:"1px solid #1a1a1a", padding:"12px" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", fontWeight:700, marginBottom:"4px" }}>{k.label}</div>
              <div style={{ color:"#fff", fontSize:"13px", fontWeight:700, marginBottom:"4px" }}>{k.val}</div>
              <div style={{ color:"#555", fontSize:"10px" }}>{k.note}</div>
            </div>
          ))}
        </div>
        <div style={{ color:"#555", fontSize:"11px", marginTop:"12px" }}>Thresholds are configurable in code — see <code style={{color:"#32CD32"}}>src/hooks/useJakeIntelligence.ts</code>.</div>
      </div>
    </>
  );
}
